import React, { useEffect, useState } from "react";
import { LuTrendingUp, LuUsers, LuCircleCheck, LuClock } from "react-icons/lu";
import { getPipelineStats, getActivationFunnel, getRmPerformance } from "../../Services/engagementService";

const StatTile = ({ icon, label, value, accent }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5">
    <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
      {icon} {label}
    </div>
    <p className="text-2xl font-bold" style={{ color: accent || "#1C2B36" }}>{value}</p>
  </div>
);

const PipelineDashboard = () => {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [rms, setRms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPipelineStats()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    getActivationFunnel().then(setFunnel).catch(() => {});
    getRmPerformance().then(setRms).catch(() => {});
  }, []);

  if (loading) return <div className="p-6 text-gray-400">Loading…</div>;
  if (!data) return <div className="p-6 text-gray-400">No data (run migration 005).</div>;

  const maxStage = Math.max(1, ...data.byStage.map((s) => s.count));
  const agingTotal = data.aging.fresh + data.aging.over3d + data.aging.over7d || 1;

  const funnelMax = funnel ? Math.max(1, ...funnel.map((s) => s.count)) : 1;

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold text-gray-800 mb-6">Lead Pipeline</h1>

      {/* Advisor activation funnel */}
      {funnel && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-8">
          <h3 className="font-semibold text-gray-800 mb-1">Advisor activation</h3>
          <p className="text-xs text-gray-400 mb-4">How many advisors reach each milestone.</p>
          <div className="space-y-3">
            {funnel.map((s) => (
              <div key={s.key} className="flex items-center gap-3">
                <span className="w-32 text-sm text-gray-600 shrink-0">{s.label}</span>
                <div className="flex-1 h-7 bg-gray-50 rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md bg-[#425F80] flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(6, (s.count / funnelMax) * 100)}%` }}
                  >
                    <span className="text-[11px] font-semibold text-white">{s.count}</span>
                  </div>
                </div>
                <span className="w-12 text-right text-sm text-gray-500">{s.percent}%</span>
                {s.dropoff > 0 && (
                  <span className="w-16 text-right text-xs text-red-500">-{s.dropoff}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatTile icon={<LuUsers size={14} />} label="Total leads" value={data.totals.total} />
        <StatTile icon={<LuClock size={14} />} label="Open" value={data.totals.open} accent="#2E86C1" />
        <StatTile icon={<LuCircleCheck size={14} />} label="Sold" value={data.totals.sold} accent="#1E7F52" />
        <StatTile icon={<LuTrendingUp size={14} />} label="Conversion" value={`${data.conversionRate}%`} accent="#425F80" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Funnel by stage */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Leads by stage</h3>
          <div className="space-y-3">
            {data.byStage.map((s) => (
              <div key={s.id} className="flex items-center gap-3">
                <span className="w-28 text-sm text-gray-600 shrink-0">{s.title}</span>
                <div className="flex-1 h-6 bg-gray-50 rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md flex items-center justify-end pr-2"
                    style={{
                      width: `${Math.max(4, (s.count / maxStage) * 100)}%`,
                      background: s.color || "#425F80",
                    }}
                  >
                    <span className="text-[11px] font-semibold text-white">{s.count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Aging */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-1">Open-lead aging</h3>
          <p className="text-xs text-gray-400 mb-4">Older leads go cold — chase the aged ones.</p>
          {[
            { k: "fresh", label: "Under 3 days", v: data.aging.fresh, c: "#1E7F52" },
            { k: "over3d", label: "3–7 days", v: data.aging.over3d, c: "#B0770C" },
            { k: "over7d", label: "Over 7 days (stale)", v: data.aging.over7d, c: "#B23A48" },
          ].map((row) => (
            <div key={row.k} className="mb-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">{row.label}</span>
                <span className="font-semibold" style={{ color: row.c }}>{row.v}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(row.v / agingTotal) * 100}%`, background: row.c }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-RM performance */}
      {rms && rms.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mt-8">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Relationship Manager performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-5 py-3">Manager</th>
                  <th className="px-5 py-3 text-right">Leads</th>
                  <th className="px-5 py-3 text-right">Sold</th>
                  <th className="px-5 py-3 text-right">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {rms.map((r) => (
                  <tr key={r.rm_id} className="border-t border-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-800">{r.name}</td>
                    <td className="px-5 py-3 text-right">{r.total}</td>
                    <td className="px-5 py-3 text-right text-green-600">{r.sold}</td>
                    <td className="px-5 py-3 text-right">
                      <span className={`font-semibold ${r.conversion >= 30 ? "text-green-600" : r.conversion >= 10 ? "text-amber-600" : "text-gray-500"}`}>
                        {r.conversion}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineDashboard;
